/**
 * Comprehensive Exam Protocol v6.0
 * 
 * Integrates both CPA and BAR exam cognitive frameworks
 * Based on 5500+ line professional exam training manual
 */

export interface ExamDomain {
  name: string;
  subjects: string[];
  framework: string;
  keyPrinciples: string[];
  commonTraps: string[];
}

/**
 * FOUNDATIONAL COGNITIVE ARCHITECTURE
 * Universal problem-solving for all exam types
 */
export const UNIVERSAL_PROBLEM_SOLVING = `
## 🧠 UNIVERSAL 5-PHASE PROBLEM-SOLVING FRAMEWORK

**PHASE 1: INTAKE & CLASSIFICATION (5-10 seconds)**
- Identify subject area (Accounting, Auditing, Tax, Law)
- Identify question type (Recall, Application, Analysis, Synthesis, Evaluation)
- Flag complexity level (Simple, Moderate, Complex, Expert)

**PHASE 2: INFORMATION EXTRACTION (10-30 seconds)**
- Parse given facts
- Identify what's required
- Note constraints & assumptions
- **Spot red herrings** (irrelevant information)
- Organize data spatially

**PHASE 3: FRAMEWORK SELECTION (5-15 seconds)**
- Invoke relevant rule/standard (GAAP, ASC, IRC, Constitutional provision, Restatement)
- Identify applicable exceptions
- Consider jurisdiction-specific variations

**PHASE 4: ANALYSIS & COMPUTATION (30-180 seconds)**
- Apply framework systematically
- Perform calculations / legal reasoning
- Consider alternative interpretations
- Chain logic steps explicitly

**PHASE 5: VALIDATION & COMMUNICATION (15-45 seconds)**
- Check reasonableness
- Verify completeness
- Structure response clearly
- Cite authority appropriately

**META-COGNITIVE CHECKS:**
✓ What am I being asked to find/prove/explain?
✓ What framework applies?
✓ Do I have all necessary information?
✓ Does this answer the question asked?
✓ Is the magnitude/outcome reasonable?
`;

/**
 * ERROR PREVENTION SYSTEM
 */
export const ERROR_PREVENTION_CHECKLIST = `
## 🚨 PRE-RESPONSE ERROR PREVENTION

**Universal Checks (ALL answers):**
- [ ] Question stem fully addressed?
- [ ] All sub-questions answered?
- [ ] Units consistent throughout?
- [ ] Arithmetic verified?
- [ ] Logic sequence coherent?
- [ ] Citations to authority included?
- [ ] Assumptions explicitly stated?

**Accounting-Specific:**
- [ ] Debits = Credits?
- [ ] Balance sheet balances?
- [ ] Reasonableness of amounts?
- [ ] Proper period covered?
- [ ] Tax vs. book differences addressed?

**Legal-Specific:**
- [ ] Correct jurisdiction applied?
- [ ] All elements of rule identified?
- [ ] Facts applied to each element?
- [ ] Counter-arguments considered?
- [ ] Policy implications noted?

**RED FLAGS (Double-Check Required):**

**Accounting Red Flags:**
- Negative retained earnings in profitable company
- Assets < Liabilities without explanation
- Enormous percentage changes (>100%) year-over-year
- Revenue recognized before performance obligation satisfied

**Legal Red Flags:**
- Defendant liable without fault (check strict liability)
- Constitutional violation without state action
- Hearsay admitted without exception
- Contract enforced despite missing element
`;

/**
 * CPA EXAM DOMAINS
 */
export const CPA_DOMAINS: Record<string, ExamDomain> = {
  FAR: {
    name: 'Financial Accounting & Reporting',
    subjects: [
      'Financial Statement Preparation',
      'Revenue Recognition (ASC 606)',
      'Leases (ASC 842)',
      'Business Combinations',
      'Consolidations',
      'Deferred Taxes',
      'Pensions',
      'Governmental Accounting',
      'Not-for-Profit'
    ],
    framework: 'GAAP / ASC Codification',
    keyPrinciples: [
      'Assets = Liabilities + Equity (always)',
      'Revenue when performance obligation satisfied',
      'Match expenses to period incurred',
      'Conservatism when uncertain',
      'Full disclosure principle'
    ],
    commonTraps: [
      'Cash basis vs Accrual basis confusion',
      'Dividends are NOT expenses (reduce RE directly)',
      'Contra accounts subtract (Accumulated Depreciation, Sales Returns)',
      'Current = <1 year OR operating cycle',
      'Adjusting entries NEVER involve Cash'
    ]
  },

  AUD: {
    name: 'Auditing & Attestation',
    subjects: [
      'Audit Planning & Risk Assessment',
      'Internal Control Evaluation',
      'Audit Evidence & Procedures',
      'Sampling',
      'Audit Reports',
      'Attestation Services',
      'Ethics & Independence',
      'Government Auditing'
    ],
    framework: 'GAAS / PCAOB / Yellow Book',
    keyPrinciples: [
      'Reasonable assurance, not absolute',
      'Professional skepticism required',
      'Independence in fact and appearance',
      'Sufficient appropriate evidence',
      'Material misstatements matter'
    ],
    commonTraps: [
      'Scope limitation vs. GAAP departure (different opinion modifications)',
      'Overstatement: Test from Ledger → Documents',
      'Understatement: Test from Documents → Ledger',
      'Emphasis-of-Matter does NOT modify opinion',
      'Independence impaired by ANY direct financial interest'
    ]
  },

  REG: {
    name: 'Regulation (Tax & Business Law)',
    subjects: [
      'Individual Taxation',
      'Corporate Taxation',
      'Partnership & S-Corp',
      'Estate & Gift Tax',
      'Tax-Exempt Organizations',
      'Business Law',
      'Ethics'
    ],
    framework: 'IRC / Treasury Regulations / UCC',
    keyPrinciples: [
      'Gross income = all income from whatever source (IRC §61)',
      'Deductions are matters of legislative grace',
      'Substance over form',
      'Capital losses limited to $3,000/year against ordinary income',
      'Basis = cost + improvements - depreciation'
    ],
    commonTraps: [
      'AGI vs. Taxable Income (different deduction stages)',
      'Standard deduction OR itemized (not both)',
      'Dividends are NOT deductible by C corp',
      'S corp debt does NOT increase shareholder basis',
      'Gift/inheritance = tax-free to recipient'
    ]
  },

  BAR: {
    name: 'Business Analysis & Reporting',
    subjects: [
      'Cost Accounting',
      'Variance Analysis',
      'Budgeting',
      'Performance Management',
      'Financial Analysis',
      'Valuation',
      'Corporate Finance',
      'Economics',
      'Data Analytics'
    ],
    framework: 'Managerial Accounting Principles',
    keyPrinciples: [
      'Relevant costs = Future costs that differ between alternatives',
      'Sunk costs are ALWAYS irrelevant',
      'Opportunity cost = benefit foregone',
      'Break-even = Fixed Costs / CM per unit',
      'Variance Unfavorable if Actual > Standard'
    ],
    commonTraps: [
      'Book value of old equipment = sunk cost (IGNORE)',
      'Allocated fixed costs often irrelevant',
      'Depreciation is non-cash (ignore unless tax shield)',
      'Contribution margin ≠ Gross margin',
      'Standard cost variance = (Actual - Standard) × Rate'
    ]
  }
};

/**
 * BAR EXAM DOMAINS
 */
export const BAR_DOMAINS: Record<string, ExamDomain> = {
  CONSTITUTIONAL: {
    name: 'Constitutional Law',
    subjects: [
      'Judicial Review',
      'Federal Legislative Power',
      'Executive Power',
      'Federalism',
      'Individual Rights',
      'Due Process',
      'Equal Protection',
      'First Amendment'
    ],
    framework: 'U.S. Constitution / Supreme Court Precedent',
    keyPrinciples: [
      'State action required for Constitutional violation',
      'Strict scrutiny: Compelling interest + narrowly tailored',
      'Intermediate scrutiny: Important interest + substantially related',
      'Rational basis: Legitimate interest + rationally related',
      'Commerce power is broad',
      'Supremacy Clause: Federal law trumps state law'
    ],
    commonTraps: [
      'Private conduct ≠ state action (no Constitutional violation)',
      'Standing requires: Injury + Causation + Redressability',
      'Prior restraint on speech presumptively invalid',
      'Content-based restrictions = strict scrutiny',
      'Takings require just compensation'
    ]
  },

  CONTRACTS: {
    name: 'Contracts',
    subjects: [
      'Formation',
      'Consideration',
      'Terms',
      'Performance',
      'Breach',
      'Remedies',
      'Third-Party Rights',
      'Defenses',
      'UCC Article 2'
    ],
    framework: 'Common Law / UCC Article 2 (Sales) / Restatement (Second)',
    keyPrinciples: [
      'Offer + Acceptance + Consideration = Contract',
      'Mirror image rule (Common Law) vs. Battle of Forms (UCC)',
      'Parol evidence rule: Prior/contemporaneous statements barred if integrated',
      'Expectation damages = put in position if performed',
      'Specific performance only if damages inadequate'
    ],
    commonTraps: [
      'UCC applies to GOODS only (not services/real estate)',
      'Past consideration = no consideration',
      'Illusory promise = no consideration',
      'Statute of Frauds: MYLEGS (Marriage, Year, Land, Executor, Goods $500+, Surety)',
      'Modification: Common Law requires consideration, UCC does not'
    ]
  },

  CRIMINAL: {
    name: 'Criminal Law & Procedure',
    subjects: [
      'Elements of Crimes',
      'Mens Rea',
      'Inchoate Crimes',
      'Defenses',
      'Fourth Amendment',
      'Fifth Amendment',
      'Sixth Amendment',
      'Trial Procedures'
    ],
    framework: 'MPC / Common Law / Constitutional Criminal Procedure',
    keyPrinciples: [
      'Actus reus + Mens rea (for most crimes)',
      'Specific intent crimes: FIAT (First degree murder, Inchoate, Assault, Theft)',
      'Search requires warrant OR exception',
      'Miranda required for custodial interrogation',
      'Right to counsel attaches at critical stages'
    ],
    commonTraps: [
      'Accomplice liability requires intent to assist + intent for crime',
      'Conspiracy does NOT merge (can be convicted of both)',
      'Reasonable expectation of privacy required for 4th Amendment',
      'Inevitable discovery = independent source exception',
      'Felony murder = strict liability for death'
    ]
  },

  EVIDENCE: {
    name: 'Evidence',
    subjects: [
      'Relevance',
      'Hearsay',
      'Exceptions',
      'Character Evidence',
      'Impeachment',
      'Authentication',
      'Best Evidence',
      'Privileges',
      'Expert Testimony'
    ],
    framework: 'Federal Rules of Evidence',
    keyPrinciples: [
      'Hearsay = Out of court statement offered for truth',
      'Hearsay is inadmissible UNLESS exception applies',
      'Relevance: Tendency to prove/disprove material fact',
      'Character evidence generally inadmissible (exceptions exist)',
      '403: Exclude if prejudice substantially outweighs probative value'
    ],
    commonTraps: [
      'Statement not offered for truth = NOT hearsay',
      'Present sense impression vs. Excited utterance (timing)',
      'Party admission = Non-hearsay (not exception)',
      'Reputation/opinion OK, specific acts generally NOT',
      'Prior inconsistent statement: Substantive if under oath'
    ]
  },

  PROPERTY: {
    name: 'Real Property',
    subjects: [
      'Estates',
      'Future Interests',
      'Concurrent Estates',
      'Landlord-Tenant',
      'Easements',
      'Covenants',
      'Adverse Possession',
      'Mortgages',
      'Recording Acts'
    ],
    framework: 'Common Law / Modern Statutes',
    keyPrinciples: [
      'Fee simple absolute = complete ownership',
      'Life estate = duration of life',
      'Rule Against Perpetuities: Interest must vest within 21 years after life in being',
      'Notice jurisdiction: Last BFP wins',
      'Race-notice: First BFP to record wins'
    ],
    commonTraps: [
      'Fee simple determinable (automatic) vs. Fee simple subject to condition subsequent (requires action)',
      'Joint tenancy requires four unities: TTIP (Time, Title, Interest, Possession)',
      'Easement appurtenant runs with land, in gross does not',
      'Adverse possession: COAH (Continuous, Open, Actual, Hostile)',
      'Recording acts only protect BFPs'
    ]
  },

  TORTS: {
    name: 'Torts',
    subjects: [
      'Intentional Torts',
      'Negligence',
      'Duty',
      'Breach',
      'Causation',
      'Damages',
      'Strict Liability',
      'Products Liability',
      'Defenses'
    ],
    framework: 'Restatement (Second) & (Third) of Torts',
    keyPrinciples: [
      'Negligence = Duty + Breach + Causation + Damages (all required)',
      'Duty: Reasonable care under circumstances',
      'But-for causation + Proximate cause (foreseeability)',
      'Strict liability: No fault required',
      'Products liability: Defect + Causation + Damages'
    ],
    commonTraps: [
      'Trespass = intent to enter, not intent to trespass',
      'Battery = intent for contact, not intent to harm',
      'Negligence per se requires violation of statute',
      'Pure economic loss generally not recoverable',
      'Joint & several liability vs. several liability (depends on jurisdiction)'
    ]
  },

  PROCEDURE: {
    name: 'Civil Procedure',
    subjects: [
      'Jurisdiction',
      'Personal Jurisdiction',
      'Subject Matter',
      'Venue',
      'Pleadings',
      'Discovery',
      'Motions',
      'Trial',
      'Appeals',
      'Preclusion'
    ],
    framework: 'Federal Rules of Civil Procedure / Erie Doctrine',
    keyPrinciples: [
      'Personal jurisdiction: Minimum contacts + Fairness',
      'Subject matter: Federal question OR Diversity (>$75K + complete diversity)',
      'Pleadings: Notice pleading standard',
      'Summary judgment: No genuine issue of material fact',
      'Res judicata: Same claim, same parties, final judgment'
    ],
    commonTraps: [
      'Long-arm statute + Constitutional minimum contacts (both required)',
      'Diversity: Complete diversity (no plaintiff from same state as any defendant)',
      'Amount in controversy: Legal certainty plaintiff cannot recover',
      'Supplemental jurisdiction: Common nucleus of operative fact',
      'Collateral estoppel: Issue actually litigated and essential'
    ]
  }
};

/**
 * Detect which domain(s) apply to a question
 */
export function detectExamDomains(question: string): {
  cpa: string[];
  bar: string[];
  needsUniversal: boolean;
} {
  const lowerQ = question.toLowerCase();
  const cpaMatches: string[] = [];
  const barMatches: string[] = [];

  // CPA Detection
  const cpaKeywords = {
    FAR: ['revenue', 'lease', 'consolidat', 'financial statement', 'gaap', 'asc', 'depreciation', 'inventory', 'goodwill'],
    AUD: ['audit', 'gaas', 'independence', 'internal control', 'sampling', 'opinion', 'attestation', 'pcaob'],
    REG: ['tax', 'irc', 'deduction', 'basis', 'partnership', 's corporation', 'estate tax', 'gift tax'],
    BAR: ['variance', 'standard cost', 'break-even', 'contribution margin', 'budget', 'cvp', 'relevant cost']
  };

  // BAR Detection
  const barKeywords = {
    CONSTITUTIONAL: ['constitutional', 'first amendment', 'due process', 'equal protection', 'commerce clause', 'supremacy'],
    CONTRACTS: ['contract', 'offer', 'acceptance', 'consideration', 'breach', 'ucc', 'parol evidence'],
    CRIMINAL: ['criminal', 'mens rea', 'actus reus', 'miranda', 'fourth amendment', 'search', 'seizure'],
    EVIDENCE: ['hearsay', 'evidence', 'relevant', 'fre', 'impeachment', 'privilege'],
    PROPERTY: ['property', 'estate', 'fee simple', 'easement', 'adverse possession', 'recording act'],
    TORTS: ['tort', 'negligence', 'duty', 'breach', 'causation', 'strict liability', 'defamation'],
    PROCEDURE: ['jurisdiction', 'venue', 'pleading', 'discovery', 'summary judgment', 'frcp']
  };

  // Check CPA
  Object.entries(cpaKeywords).forEach(([domain, keywords]) => {
    if (keywords.some(kw => lowerQ.includes(kw))) {
      cpaMatches.push(domain);
    }
  });

  // Check BAR
  Object.entries(barKeywords).forEach(([domain, keywords]) => {
    if (keywords.some(kw => lowerQ.includes(kw))) {
      barMatches.push(domain);
    }
  });

  return {
    cpa: cpaMatches,
    bar: barMatches,
    needsUniversal: cpaMatches.length > 0 || barMatches.length > 0
  };
}

/**
 * Generate comprehensive exam protocol instruction
 */
export function generateComprehensiveExamInstruction(question: string): string {
  const domains = detectExamDomains(question);
  
  if (domains.cpa.length === 0 && domains.bar.length === 0) {
    return ''; // Not an exam question
  }

  let instruction = '';

  // Add universal framework if applicable
  if (domains.needsUniversal) {
    instruction += UNIVERSAL_PROBLEM_SOLVING + '\n\n';
    instruction += ERROR_PREVENTION_CHECKLIST + '\n\n';
  }

  // Add CPA domain specifics
  if (domains.cpa.length > 0) {
    instruction += '## 📚 CPA EXAM DOMAINS ACTIVATED:\n\n';
    domains.cpa.forEach(domain => {
      const domainInfo = CPA_DOMAINS[domain];
      instruction += `### ${domainInfo.name}\n`;
      instruction += `**Framework:** ${domainInfo.framework}\n\n`;
      instruction += `**Key Principles:**\n${domainInfo.keyPrinciples.map(p => `- ${p}`).join('\n')}\n\n`;
      instruction += `**⚠️ Common Traps:**\n${domainInfo.commonTraps.map(t => `- ${t}`).join('\n')}\n\n`;
    });
  }

  // Add BAR domain specifics
  if (domains.bar.length > 0) {
    instruction += '## ⚖️ BAR EXAM DOMAINS ACTIVATED:\n\n';
    domains.bar.forEach(domain => {
      const domainInfo = BAR_DOMAINS[domain];
      instruction += `### ${domainInfo.name}\n`;
      instruction += `**Framework:** ${domainInfo.framework}\n\n`;
      instruction += `**Key Principles:**\n${domainInfo.keyPrinciples.map(p => `- ${p}`).join('\n')}\n\n`;
      instruction += `**⚠️ Common Traps:**\n${domainInfo.commonTraps.map(t => `- ${t}`).join('\n')}\n\n`;
    });
  }

  return instruction;
}

/**
 * Check if question requires comprehensive exam protocol
 */
export function requiresComprehensiveProtocol(question: string): boolean {
  const domains = detectExamDomains(question);
  return domains.cpa.length > 0 || domains.bar.length > 0;
}
